/**
 * Pure schedule logic behind the automation editor: the picker's five modes and the cron
 * expression they produce, plus the reverse (a stored expression → the picker).
 * Kept out of the page so it can be unit tested (tests/web/automation-schedule.test.ts).
 */

export const PRESETS = ['daily', 'weekly', 'monthly', 'interval', 'custom'] as const
export type Preset = typeof PRESETS[number]

/** Cron weekdays in the order the picker shows them: Monday (1) … Sunday (0, last). */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0]
export const WEEKDAY_LABEL: Record<number, string> = { 0: 'schedule.sun', 1: 'schedule.mon', 2: 'schedule.tue', 3: 'schedule.wed', 4: 'schedule.thu', 5: 'schedule.fri', 6: 'schedule.sat' }

export interface Schedule {
  preset: Preset
  /** "HH:mm" for daily/weekly/monthly (and the minute offset of an hourly interval). */
  time: string
  /** Raw cron, only used by the custom preset. */
  custom: string
  /** Cron weekdays (0 = Sunday); used by weekly. */
  weekdays: number[]
  /** Days of the month 1–31; used by monthly. */
  monthDays: number[]
  /** Interval size; used by interval. */
  interval: number
  intervalUnit: 'minutes' | 'hours'
}

/** 1,2,3,4,5 → "1-5"; 1,3,5 → "1,3,5"; every day of the range → "*". */
export function dayList(days: number[], max: 7 | 31): string {
  const min = max === 7 ? 0 : 1
  const total = max === 7 ? 7 : 31 // weekdays are 0–6, so the count is not max - min + 1
  const set = [...new Set(days)].filter(n => n >= min && n <= max).sort((a, b) => a - b)
  if (set.length === 0) return '1'
  if (set.length === total) return '*'
  const out: string[] = []
  for (let i = 0; i < set.length;) {
    let j = i
    while (j + 1 < set.length && set[j + 1] === set[j] + 1) j++
    out.push(j - i >= 2 ? `${set[i]}-${set[j]}` : set.slice(i, j + 1).join(','))
    i = j + 1
  }
  return out.join(',')
}

/** "1-5,0" → [0,1,2,3,4,5]; cron's 7 is treated as Sunday. */
export function parseDays(spec: string, max: 7 | 31): number[] {
  const min = max === 7 ? 0 : 1
  const out = new Set<number>()
  const add = (n: number) => out.add(n === 7 && max === 7 ? 0 : n)
  for (const part of spec.split(',')) {
    const range = /^(\d+)-(\d+)$/.exec(part.trim())
    if (range) { for (let n = Number(range[1]); n <= Number(range[2]); n++) add(n) }
    else if (/^\d+$/.test(part.trim())) add(Number(part))
  }
  return [...out].filter(n => n >= min && n <= max).sort((a, b) => a - b)
}

export function exprFor(d: Schedule): string {
  const [h, m] = d.time.split(':').map(n => String(Number(n) || 0))
  switch (d.preset) {
    case 'daily': return `${m} ${h} * * *`
    case 'weekly': return `${m} ${h} * * ${dayList(d.weekdays, 7)}`
    case 'monthly': return `${m} ${h} ${dayList(d.monthDays, 31)} * *`
    case 'interval': return d.intervalUnit === 'hours'
      ? `${m} */${Math.max(1, d.interval)} * * *`
      : `*/${Math.max(1, d.interval)} * * * *`
    case 'custom': return d.custom
  }
}

/** Reads a cron expression back into the picker; anything it cannot express stays "custom". */
export function guessSchedule(expr: string): Partial<Schedule> {
  const minutes = /^\*\/(\d+) \* \* \* \*$/.exec(expr)
  if (minutes) return { preset: 'interval', interval: Number(minutes[1]), intervalUnit: 'minutes' }
  const hours = /^(\d+) \*\/(\d+) \* \* \*$/.exec(expr)
  if (hours) return { preset: 'interval', interval: Number(hours[2]), intervalUnit: 'hours', time: `00:${hours[1].padStart(2, '0')}` }
  const m = /^(\d+) (\d+) (\S+) \* (\S+)$/.exec(expr)
  if (!m) return { preset: 'custom' }
  const time = `${m[2].padStart(2, '0')}:${m[1].padStart(2, '0')}`
  if (m[3] === '*' && m[4] === '*') return { preset: 'daily', time }
  if (m[3] === '*') return { preset: 'weekly', time, weekdays: parseDays(m[4], 7) }
  if (m[4] === '*') return { preset: 'monthly', time, monthDays: parseDays(m[3], 31) }
  return { preset: 'custom' }
}

/**
 * One-line summary of a cron expression in the picker's own words ("Hằng tuần T2, T4 lúc 10:00"),
 * or null when the expression is not something the picker can express (custom cron).
 */
export function describeSchedule(expr: string, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  const g = guessSchedule(expr)
  switch (g.preset) {
    case 'daily': return t('automations.schedule.summaryDaily', { time: g.time })
    case 'weekly': return t('automations.schedule.summaryWeekly', { time: g.time, days: (g.weekdays ?? []).map(d => t(`automations.${WEEKDAY_LABEL[d]}`)).join(', ') })
    case 'monthly': return t('automations.schedule.summaryMonthly', { time: g.time, days: (g.monthDays ?? []).join(', ') })
    case 'interval': return g.intervalUnit === 'hours'
      ? t('automations.schedule.summaryHours', { n: g.interval })
      : t('automations.schedule.summaryMinutes', { n: g.interval })
    default: return null
  }
}

/** Picker state for a routine that has no cron trigger yet (Mon–Fri at 08:00, as before). */
export const DEFAULT_SCHEDULE: Schedule = {
  preset: 'weekly', time: '08:00', custom: '0 8 * * 1-5',
  weekdays: [1, 2, 3, 4, 5], monthDays: [1], interval: 30, intervalUnit: 'minutes'
}
